import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Input, Button, Upload, Progress, Tag, message, Tooltip } from 'antd';
import {
  FilePdfOutlined,
  InboxOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  CloudUploadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { uploadProjectDocument } from '../../../services/projectServices';

const ProjectDocumentUploadModal = ({
  open,
  visible,
  onClose,
  projectId,
  existingDocumentsCount = 0,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const isOpen = Boolean(open ?? visible);

  // Array of files: { id, file, name, size, status: 'ready' | 'uploading' | 'done' | 'error' }
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentFilePercent, setCurrentFilePercent] = useState(0);
  const [overallPercent, setOverallPercent] = useState(0);

  const cleanFileName = (filename, index = 1) => {
    if (!filename) return `Document ${index}`;
    const base = filename.replace(/\.pdf$/i, '').trim();

    // Detect system UUIDs, camera hashes, or timestamps (e.g. 490_1791449995_3b94973b-ff82...)
    const isSystemHash =
      /[0-9a-f]{8}-[0-9a-f]{4}/i.test(base) ||
      /^\d+_\d+_[a-z0-9]+/i.test(base) ||
      /^[0-9a-f]{20,}/i.test(base);

    if (isSystemHash) {
      const dateStr = moment().format('MMM D, YYYY');
      return `Scan ${index} — ${dateStr}`;
    }

    const cleaned = base.replace(/[_-]+/g, ' ').trim();
    return cleaned
      .split(' ')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (isOpen) {
      setFileList([]);
      setUploading(false);
      setCurrentIndex(0);
      setCurrentFilePercent(0);
      setOverallPercent(0);
    }
  }, [isOpen]);

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const handleBeforeUpload = (file, fileListBatch) => {
    const isPdf =
      file.type === 'application/pdf' ||
      (file.name && file.name.toLowerCase().endsWith('.pdf'));

    if (!isPdf) {
      message.error(`"${file.name}" is not a PDF. Only PDF files are supported.`);
      return Upload.LIST_IGNORE;
    }

    const isLt100M = file.size / 1024 / 1024 < 100;
    if (!isLt100M) {
      message.error(`"${file.name}" is larger than 100MB.`);
      return Upload.LIST_IGNORE;
    }

    setFileList((prev) => {
      // Avoid duplicate by name & size
      const exists = prev.some(
        (item) => item.file.name === file.name && item.file.size === file.size
      );
      if (exists) return prev;

      const nextIndex = prev.length + 1;
      const defaultName = cleanFileName(file.name, nextIndex);
      const newId = `${file.name}_${file.size}_${Date.now()}_${Math.random()}`;

      const newEntry = {
        id: newId,
        file,
        name: defaultName,
        size: file.size,
        status: 'ready',
      };

      return [...prev, newEntry];
    });

    return false; // Prevent automatic upload by AntD
  };

  const handleUpdateName = (id, newName) => {
    setFileList((prev) =>
      prev.map((item) => (item.id === id ? { ...item, name: newName } : item))
    );
  };

  const handleRemoveFile = (id) => {
    setFileList((prev) => prev.filter((item) => item.id !== id));
  };

  const handleClearAll = () => {
    setFileList([]);
  };

  const totalSize = fileList.reduce((acc, item) => acc + (item.size || 0), 0);

  const detectPdfPages = async (file) => {
    try {
      const text = await file.text();
      const matches = text.match(/\/Type\s*\/Page[^s]/gi);
      if (matches && matches.length > 0) {
        return matches.length;
      }
      const countMatch = text.match(/\/Count\s+(\d+)/i);
      if (countMatch && countMatch[1]) {
        const parsed = parseInt(countMatch[1], 10);
        if (parsed > 0) return parsed;
      }
    } catch (e) {
      console.warn('Failed to detect PDF page count on upload:', e);
    }
    return 1;
  };

  const handleExecuteUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select at least one PDF file to upload.');
      return;
    }

    // Verify all files have a name
    for (const item of fileList) {
      if (!item.name || !item.name.trim()) {
        message.error(`Please provide a title for "${item.file.name}".`);
        return;
      }
    }

    setUploading(true);
    setCurrentIndex(0);
    setCurrentFilePercent(0);
    setOverallPercent(0);

    const uploadedResults = [];
    const totalFiles = fileList.length;

    try {
      for (let i = 0; i < totalFiles; i++) {
        const item = fileList[i];
        setCurrentIndex(i);
        setCurrentFilePercent(15);

        // Update item status in list
        setFileList((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading' } : f))
        );

        const safeName = item.name.trim();
        const pageCount = await detectPdfPages(item.file);
        const formData = new FormData();
        const sanitizedFilename = `${projectId}_${Date.now()}_${safeName.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;

        formData.append('file', item.file, sanitizedFilename);
        formData.append('name', safeName);
        formData.append('page_count', String(pageCount));
        formData.append('project_id', String(projectId));

        const uploadedDoc = await dispatch(
          uploadProjectDocument(projectId, formData, (percent) => {
            setCurrentFilePercent(percent);
            const overall = Math.round(((i + percent / 100) / totalFiles) * 100);
            setOverallPercent(overall);
          })
        );

        uploadedResults.push(uploadedDoc);

        // Mark item as done
        setFileList((prev) =>
          prev.map((f, idx) => (idx === i ? { ...f, status: 'done' } : f))
        );
      }

      setOverallPercent(100);
      setCurrentFilePercent(100);

      const msg =
        uploadedResults.length === 1
          ? `"${uploadedResults[0]?.name || 'Document'}" uploaded successfully!`
          : `All ${uploadedResults.length} documents uploaded successfully!`;
      message.success(msg);

      if (onSuccess) {
        onSuccess(uploadedResults);
      }

      setTimeout(() => {
        onClose();
      }, 400);
    } catch (err) {
      console.error('Document multi-upload error:', err);
      const errMsg =
        err?.response?.data?.message ||
        (err?.response?.data?.errors
          ? Object.values(err.response.data.errors).flat().join(', ')
          : null) ||
        'Failed to upload document';
      message.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
              border: '1px solid #fca5a5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              fontSize: 18,
              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.08)',
              flexShrink: 0,
            }}
          >
            <FilePdfOutlined />
          </div>
          <div>
            <div style={{ fontSize: 16.5, fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
              Upload Project Documents
            </div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 400, marginTop: 2 }}>
              Attach inspection scans, blueprints or multi-page PDF reports
            </div>
          </div>
        </div>
      }
      open={isOpen}
      visible={isOpen}
      onCancel={() => !uploading && onClose()}
      footer={
        <div style={{ paddingTop: 8 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              borderTop: '1px solid #f1f5f9',
              paddingTop: 12,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ fontSize: 12, color: '#64748b' }}>
              {fileList.length > 0 && (
                <span>
                  <strong>{fileList.length}</strong> {fileList.length === 1 ? 'file' : 'files'} selected ·{' '}
                  {formatFileSize(totalSize)}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Button
                onClick={onClose}
                disabled={uploading}
                style={{
                  borderRadius: 8,
                  height: 38,
                  fontWeight: 500,
                  color: '#475569',
                  borderColor: '#cbd5e1',
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={!uploading && <CloudUploadOutlined style={{ fontSize: 16 }} />}
                onClick={handleExecuteUpload}
                loading={uploading}
                disabled={fileList.length === 0 || uploading}
                style={{
                  background:
                    fileList.length === 0 || uploading
                      ? '#94a3b8'
                      : 'linear-gradient(135deg, #0038d1 0%, #1d4ed8 100%)',
                  borderColor: 'transparent',
                  borderRadius: 8,
                  height: 38,
                  padding: '0 18px',
                  fontWeight: 600,
                  boxShadow:
                    fileList.length > 0 && !uploading
                      ? '0 3px 10px rgba(0, 56, 209, 0.28)'
                      : 'none',
                }}
              >
                {uploading
                  ? `Uploading (${currentIndex + 1}/${fileList.length}) · ${overallPercent}%`
                  : fileList.length > 1
                  ? `Upload All (${fileList.length} Documents)`
                  : 'Upload Document'}
              </Button>
            </div>
          </div>
        </div>
      }
      width={typeof window !== 'undefined' && window.innerWidth < 640 ? '95%' : 580}
      style={{ top: 20, maxWidth: 620, paddingBottom: 0 }}
      destroyOnClose={true}
    >
      <div style={{ paddingTop: 8 }}>
        {/* Upload Dragger Zone */}
        {!uploading && (
          <Upload.Dragger
            multiple={true}
            accept=".pdf,application/pdf"
            beforeUpload={handleBeforeUpload}
            showUploadList={false}
            disabled={uploading}
            style={{
              padding: fileList.length === 0 ? '24px 16px' : '16px',
              borderRadius: 12,
              border: '2px dashed #93c5fd',
              background: '#f8fafc',
              cursor: 'pointer',
              marginBottom: 16,
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px',
                  color: '#0038d1',
                  fontSize: 22,
                }}
              >
                <InboxOutlined />
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 3 }}>
                {fileList.length === 0
                  ? 'Click or drag PDF files here to upload'
                  : '+ Add more PDF files to upload'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Supports multiple Adobe PDF documents up to 100 MB each
              </div>
            </div>
          </Upload.Dragger>
        )}

        {/* Selected Files List (Multi-File Queue) */}
        {fileList.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  Selected Documents ({fileList.length})
                </span>
                <Tag color="blue" style={{ borderRadius: 6, margin: 0, fontSize: 11 }}>
                  {formatFileSize(totalSize)}
                </Tag>
              </div>

              {!uploading && (
                <Button
                  type="link"
                  size="small"
                  onClick={handleClearAll}
                  style={{ color: '#ef4444', padding: 0, fontSize: 12 }}
                >
                  Clear all
                </Button>
              )}
            </div>

            {/* Scrollable List of Files */}
            <div
              style={{
                maxHeight: 250,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                paddingRight: 4,
              }}
            >
              {fileList.map((item, idx) => {
                const isUploadingThis = uploading && currentIndex === idx;
                const isDone = item.status === 'done';

                return (
                  <div
                    key={item.id}
                    style={{
                      background: isUploadingThis ? '#eff6ff' : '#f8fafc',
                      border: `1.5px solid ${isUploadingThis ? '#60a5fa' : isDone ? '#86efac' : '#e2e8f0'}`,
                      borderRadius: 10,
                      padding: '10px 12px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: isDone ? '#dcfce7' : '#fee2e2',
                          color: isDone ? '#16a34a' : '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 15,
                          flexShrink: 0,
                        }}
                      >
                        {isDone ? <CheckCircleFilled /> : <FilePdfOutlined />}
                      </div>

                      {/* File Name & Size */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            fontWeight: 600,
                            color: '#1e293b',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.file.name}
                        >
                          {item.file.name}
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          {formatFileSize(item.size)}
                          {isUploadingThis && (
                            <span style={{ color: '#0038d1', marginLeft: 8, fontWeight: 600 }}>
                              <LoadingOutlined spin /> Uploading {currentFilePercent}%...
                            </span>
                          )}
                          {isDone && (
                            <span style={{ color: '#16a34a', marginLeft: 8, fontWeight: 600 }}>
                              ✓ Uploaded
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Delete File Button */}
                      {!uploading && (
                        <Tooltip title="Remove file">
                          <Button
                            type="text"
                            size="small"
                            icon={<DeleteOutlined style={{ color: '#94a3b8' }} />}
                            onClick={() => handleRemoveFile(item.id)}
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          />
                        </Tooltip>
                      )}
                    </div>

                    {/* Editable Document Title Input */}
                    {!uploading && (
                      <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #f1f5f9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                          <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: 5 }}>
                            <EditOutlined style={{ color: '#0038d1', fontSize: 11 }} />
                            <span>Document Title / Name</span>
                          </label>
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>
                            Visible in viewer
                          </span>
                        </div>
                        <Input
                          value={item.name}
                          onChange={(e) => handleUpdateName(item.id, e.target.value)}
                          placeholder="Enter document title"
                          maxLength={150}
                          allowClear
                          style={{
                            height: 36,
                            borderRadius: 8,
                            border: '1.5px solid #cbd5e1',
                            fontSize: 13,
                            fontWeight: 500,
                            background: '#ffffff',
                            padding: '0 10px',
                            boxShadow: 'none',
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Upload Progress Bar Card */}
        {uploading && (
          <div
            style={{
              marginTop: 10,
              background: '#f8fafc',
              border: '1.5px solid #cbd5e1',
              borderRadius: 12,
              padding: '14px 18px',
              boxShadow: '0 2px 8px rgba(0, 56, 209, 0.06)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CloudUploadOutlined style={{ color: '#0038d1', fontSize: 16 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  Uploading Document {currentIndex + 1} of {fileList.length}...
                </span>
              </div>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#0038d1',
                  background: '#eff6ff',
                  padding: '2px 8px',
                  borderRadius: 8,
                }}
              >
                {overallPercent}%
              </span>
            </div>
            <Progress
              percent={overallPercent}
              showInfo={false}
              status="active"
              strokeColor={{
                '0%': '#0038d1',
                '100%': '#2563eb',
              }}
            />
            <div
              style={{
                fontSize: 11.5,
                color: '#64748b',
                textAlign: 'center',
                marginTop: 6,
                fontWeight: 500,
              }}
            >
              Currently uploading "{fileList[currentIndex]?.name || 'Document'}" · Please wait...
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ProjectDocumentUploadModal;
